import React, { useEffect, useRef, useState } from 'react';

// Detect if device is mobile
const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        || window.innerWidth < 768;
};

export const ThreeBackground = () => {
    const canvasRef = useRef(null);
    const [isMobile] = useState(isMobileDevice());

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d', {
            alpha: true,
            // Disable antialiasing on mobile for better performance
            antialias: !isMobile
        });
        if (!ctx) return;

        // Set canvas size
        const setCanvasSize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        setCanvasSize();

        // Particle system
        class Particle {
            constructor() {
                this.x = (Math.random() - 0.5) * canvas.width * 2;
                this.y = (Math.random() - 0.5) * canvas.height * 2;
                this.z = Math.random() * 1000;
                this.vx = (Math.random() - 0.5) * 0.5;
                this.vy = (Math.random() - 0.5) * 0.5;
                this.vz = Math.random() * 2;
                this.size = Math.random() * 2;
            }

            update() {
                this.x += this.vx;
                this.y += this.vy;
                this.z -= this.vz;

                if (this.z < 1) {
                    this.z = 1000;
                    this.x = (Math.random() - 0.5) * canvas.width * 2;
                    this.y = (Math.random() - 0.5) * canvas.height * 2;
                }
            }

            draw(ctx, mouseX, mouseY, skipMouseInteraction) {
                const scale = 1000 / (1000 + this.z);
                const x2d = this.x * scale + canvas.width / 2;
                const y2d = this.y * scale + canvas.height / 2;
                const size = this.size * scale;

                // Skip expensive mouse interaction on mobile
                if (!skipMouseInteraction) {
                    const dx = x2d - mouseX;
                    const dy = y2d - mouseY;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    const maxDist = 150;

                    if (dist < maxDist) {
                        const force = (maxDist - dist) / maxDist;
                        const opacity = 0.3 + force * 0.7;
                        ctx.fillStyle = `rgba(6, 182, 212, ${opacity})`;
                    } else {
                        const opacity = 0.3 + (1 - this.z / 1000) * 0.5;
                        ctx.fillStyle = `rgba(6, 182, 212, ${opacity})`;
                    }
                } else {
                    // Simpler opacity calculation for mobile
                    const opacity = 0.3 + (1 - this.z / 1000) * 0.5;
                    ctx.fillStyle = `rgba(6, 182, 212, ${opacity})`;
                }

                ctx.beginPath();
                ctx.arc(x2d, y2d, size, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        // Create particles - fewer on mobile for better performance
        const particleCount = isMobile ? 50 : 300;
        const particles = [];
        for (let i = 0; i < particleCount; i++) {
            particles.push(new Particle());
        }

        // Mouse position
        let mouseX = canvas.width / 2;
        let mouseY = canvas.height / 2;

        const handleMouseMove = (e) => {
            mouseX = e.clientX;
            mouseY = e.clientY;
        };

        // Only add mouse listener on desktop
        if (!isMobile) {
            window.addEventListener('mousemove', handleMouseMove);
        }

        // Animation loop with frame rate throttling for mobile
        let animationId;
        let lastFrameTime = 0;
        const targetFPS = isMobile ? 30 : 60; // Lower FPS on mobile
        const frameInterval = 1000 / targetFPS;

        const animate = (currentTime) => {
            animationId = requestAnimationFrame(animate);

            const elapsed = currentTime - lastFrameTime;
            if (elapsed < frameInterval) return;

            lastFrameTime = currentTime - (elapsed % frameInterval);

            ctx.fillStyle = 'rgba(15, 23, 42, 0.1)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            particles.forEach((particle) => {
                particle.update();
                particle.draw(ctx, mouseX, mouseY, isMobile);
            });
        };
        animate(0);

        // Resize handler with debounce
        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                setCanvasSize();
            }, 150);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (!isMobile) {
                window.removeEventListener('mousemove', handleMouseMove);
            }
            cancelAnimationFrame(animationId);
            clearTimeout(resizeTimeout);
        };
    }, [isMobile]);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 z-0 pointer-events-none"
            style={{
                background: 'linear-gradient(to bottom, #0F172A, #020617)',
            }}
        />
    );
};
